import React, { useMemo, useState } from 'react';
import {
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Label,
} from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useMollieSettlements } from '../../../hooks/useAdminReconciliation';
import type { MollieSettlement, MollieSettlementPeriod } from '../../../types/models/Reconciliation';
import { formatInstantBrussels, formatMonthYear } from '../../../utils/dateFormat';
import {
  AMOUNT_CELL,
  CsvButton,
  EmptyListState,
  FetchedAt,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  SettlementRef,
  StaleDataAlert,
  YearPicker,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import { SETTLEMENT_STATUS_LABELS, labelOf } from './reconciliationLabels';
import { buildSettlementsCsv, reconciliationCsvFilename } from './reconciliationCsv';

const matchSettlement = (s: MollieSettlement, q: string) =>
  matchesQuery(q, [s.reference, s.id, s.periods.map((p) => p.mollie_invoice_reference ?? '')]);

/** A payout's status: green once paid out, orange while on its way, red when it failed. */
export const SettlementStatusLabel: React.FunctionComponent<{ status: string | null }> = ({ status }) => {
  const color = status === 'paidout' ? 'green' : status === 'failed' ? 'red' : status === null ? 'grey' : 'orange';
  return (
    <Label color={color} isCompact>
      {labelOf(SETTLEMENT_STATUS_LABELS, status)}
    </Label>
  );
};

/** A payout's periods: revenue and costs per calendar month, each with that month's Mollie invoice. */
export const SettlementPeriodsTable: React.FunctionComponent<{
  periods: MollieSettlementPeriod[];
  ariaLabel: string;
}> = ({ periods, ariaLabel }) => (
  <Table aria-label={ariaLabel} variant="compact">
    <Thead>
      <Tr>
        <Th>Période</Th>
        <Th>Facture Mollie</Th>
        <Th className={AMOUNT_CELL}>Transactions</Th>
        <Th className={AMOUNT_CELL}>Revenus</Th>
        <Th className={AMOUNT_CELL}>Frais de transaction</Th>
        <Th className={AMOUNT_CELL}>Arrondi</Th>
        <Th className={AMOUNT_CELL}>Compensation</Th>
        <Th className={AMOUNT_CELL}>Remise</Th>
        <Th className={AMOUNT_CELL}>Autre</Th>
        <Th className={AMOUNT_CELL}>Frais</Th>
      </Tr>
    </Thead>
    <Tbody>
      {periods.map((p) => (
        <Tr key={p.period}>
          <Td dataLabel="Période">{formatMonthYear(p.period)}</Td>
          <Td dataLabel="Facture Mollie">
            {p.mollie_invoice_reference ?? '—'}
            {p.mollie_invoice_reference !== null && p.mollie_invoice_id === null && (
              <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle"> (pas encore émise)</span>
            )}
          </Td>
          <Td dataLabel="Transactions" className={AMOUNT_CELL}>
            {p.revenue_count}
          </Td>
          <Td dataLabel="Revenus" className={AMOUNT_CELL}>
            <Money value={p.revenue} />
          </Td>
          <Td dataLabel="Frais de transaction" className={AMOUNT_CELL}>
            <Money value={p.costs_by_class.fee} />
          </Td>
          <Td dataLabel="Arrondi" className={AMOUNT_CELL}>
            <Money value={p.costs_by_class.rounding} />
          </Td>
          <Td dataLabel="Compensation" className={AMOUNT_CELL}>
            <Money value={p.costs_by_class.compensation} />
          </Td>
          <Td dataLabel="Remise" className={AMOUNT_CELL}>
            <Money value={p.costs_by_class.discount} />
          </Td>
          <Td dataLabel="Autre" className={AMOUNT_CELL}>
            <Money value={p.costs_by_class.other} />
          </Td>
          <Td dataLabel="Frais" className={AMOUNT_CELL}>
            <Money value={p.costs} />
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const COLUMN_COUNT = 8;

interface SettlementsTabProps {
  year: number;
  onYearChange: (year: number) => void;
  isActive: boolean;
}

/**
 * Versements — `GET /admin/reconciliation/mollie/settlements`: the payouts (one bank line each) of the
 * year, with their per-month periods; each links to its breakdown.
 */
const SettlementsTab: React.FunctionComponent<SettlementsTabProps> = ({ year, onYearChange, isActive }) => {
  const { data, loading, error, fetchedAt, refetch } = useMollieSettlements(year, isActive);
  const rows = useMemo(() => data?.settlements ?? [], [data]);
  const list = useClientList(rows, matchSettlement);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <YearPicker year={year} onChange={onYearChange} />
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
    </Flex>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState title="Impossible de charger les versements" message={error} onRetry={() => void refetch()} />
        ) : (
          <LiveReadSpinner what="versements" />
        )}
      </>
    );
  }

  return (
    <>
      {header}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners warnings={data.warnings} onRetry={() => void refetch()} />
      <DescriptionList
        isCompact
        isHorizontal
        columnModifier={{ default: '1Col', md: '3Col' }}
        className="pf-v6-u-mb-md"
      >
        <DescriptionListGroup>
          <DescriptionListTerm>Versements de {year}</DescriptionListTerm>
          <DescriptionListDescription>{data.totals.count}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Total versé sur le compte bancaire</DescriptionListTerm>
          <DescriptionListDescription>
            <strong>
              <Money value={data.totals.amount} />
            </strong>
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Pas encore versé (en attente, échoué)</DescriptionListTerm>
          <DescriptionListDescription>
            <Money value={data.totals.not_paid_out_amount} />
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {rows.length === 0 ? (
        <EmptyListState title={`Aucun versement en ${year}`} />
      ) : (
        <>
          <ListToolbar
            list={list}
            placeholder="Rechercher par référence bancaire, identifiant ou facture Mollie..."
            actions={
              <CsvButton
                label="Exporter l'année (CSV)"
                filename={reconciliationCsvFilename.settlements(year)}
                build={() => buildSettlementsCsv(rows)}
              />
            }
          />
          {list.filtered.length === 0 ? (
            <NoMatchState onClear={() => list.setSearchValue('')} />
          ) : (
            <>
              <Table aria-label="Versements Mollie de l'année" variant="compact" isExpandable>
                <Thead>
                  <Tr>
                    <Th screenReaderText="Périodes" />
                    <Th>Référence bancaire</Th>
                    <Th>Statut</Th>
                    <Th>Versé le</Th>
                    <Th className={AMOUNT_CELL}>Transactions</Th>
                    <Th className={AMOUNT_CELL}>Revenus</Th>
                    <Th className={AMOUNT_CELL}>Frais</Th>
                    <Th className={AMOUNT_CELL}>Montant versé</Th>
                  </Tr>
                </Thead>
                {list.paginated.map((s, rowIndex) => {
                  const isExpanded = expanded.has(s.id);
                  return (
                    <Tbody key={s.id} isExpanded={isExpanded}>
                      <Tr isContentExpanded={isExpanded}>
                        <Td
                          expand={
                            s.periods.length > 0
                              ? { rowIndex, isExpanded, onToggle: () => toggle(s.id), expandId: 'settlement-row' }
                              : undefined
                          }
                        />
                        <Td dataLabel="Référence bancaire">
                          <SettlementRef id={s.id} reference={s.reference} />
                        </Td>
                        <Td dataLabel="Statut">
                          <SettlementStatusLabel status={s.status} />
                        </Td>
                        <Td dataLabel="Versé le" modifier="nowrap">
                          {s.settled_at === null ? (
                            <span className="pf-v6-u-text-color-subtle">
                              créé le {formatInstantBrussels(s.created_at)}
                            </span>
                          ) : (
                            formatInstantBrussels(s.settled_at)
                          )}
                        </Td>
                        <Td dataLabel="Transactions" className={AMOUNT_CELL}>
                          {s.transactions_count}
                        </Td>
                        <Td dataLabel="Revenus" className={AMOUNT_CELL}>
                          <Money value={s.revenue} />
                        </Td>
                        <Td dataLabel="Frais" className={AMOUNT_CELL}>
                          <Money value={s.costs} />
                        </Td>
                        <Td dataLabel="Montant versé" className={AMOUNT_CELL}>
                          <strong>
                            <Money value={s.amount} />
                          </strong>
                        </Td>
                      </Tr>
                      {s.periods.length > 0 && (
                        <Tr isExpanded={isExpanded}>
                          <Td />
                          <Td colSpan={COLUMN_COUNT - 1}>
                            <ExpandableRowContent>
                              <SettlementPeriodsTable
                                periods={s.periods}
                                ariaLabel={`Périodes du versement ${s.reference ?? s.id}`}
                              />
                            </ExpandableRowContent>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  );
                })}
              </Table>
              <ListPagination list={list} variant="bottom" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default SettlementsTab;
